import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useDeleteUser } from "@/api/auth-query";
import { useUserProfile } from "@/api/users-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useAuthStore from "@/store/auth";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardTitle } from "../ui/card";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { Input } from "../ui/input";
import EditProfile from "../vendor/edit-profile";
import ProfileHeader from "../vendor/profile-header";
import { AlertDialog } from "./alert-dialog";
import AddressConfirmation from "./settings/address-confirmation";

export function SettingsPage() {
	const user = useAuthStore(state => state.user);
	const { data: userProfile } = useUserProfile();

	const deleteMutation = useDeleteUser();

	return (
		<div className="flex flex-col min-h-screen">
			<header className="flex h-auto flex-col py-2">
				<Tabs defaultValue="my-account" className="w-full">
					<TabsList className="flex h-auto w-full justify-start gap-6 rounded-none border-b bg-transparent p-0">
						<TabsTrigger
							value="my-account"
							className="rounded-none border-b-2 border-transparent px-0 pb-4 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
						>
							My Account
						</TabsTrigger>
						<TabsTrigger
							value="security"
							className="rounded-none border-b-2 border-transparent px-0 pb-4 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
						>
							Security
						</TabsTrigger>
						<TabsTrigger
							value="kyc"
							className="rounded-none border-b-2 border-transparent px-0 pb-4 pt-2 font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
						>
							KYC
						</TabsTrigger>
					</TabsList>

					<TabsContent value="my-account">
						<section className="mt-8 space-y-2">
							<ProfileHeader imageUrl={userProfile?.profileImage ?? "/placeholder.svg"} name="" ratingInfo="" />

							<div className="flex gap-2 flex-wrap">
								<Card className="flex-1 min-w-80 shadow-none">
									<CardTitle className="border-b-2 p-6">Profile Details</CardTitle>
									<CardContent className="space-y-8 my-4">
										<div className="space-y-8">
											<p className="flex flex-wrap gap-2">
												<span className="text-muted-foreground">Fullname:</span>
												<span>{userProfile?.fullName}</span>
											</p>
											<p className="flex flex-wrap gap-2">
												<span className="text-muted-foreground">Email address:</span>
												<span>{user?.email}</span>
											</p>
											<p className="flex flex-wrap gap-2">
												<span className="text-muted-foreground">Address:</span>
												<span>{user?.address}</span>
											</p>
											<p className="flex flex-wrap gap-2">
												<span className="text-muted-foreground">Phone number:</span>
												<span>{userProfile?.phoneNumber}</span>
											</p>
										</div>
										<div className="flex justify-between w-full">

											<AlertDialog
												description="This action cannot be undone. This will permanently delete your account."
												onClick={() => {
													if (!user?.id)
														return;
													toast.promise(deleteMutation.mutateAsync(user.id), {
														loading: "Deleting account...",
														success: "Account deleted successfully",
														error: "Failed to delete account"
													});
												}}
											>
												<Button
													variant="destructive"

												>
													Delete Account
												</Button>
											</AlertDialog>

											<EditProfile userProfile={userProfile} />
										</div>
									</CardContent>
								</Card>

								<Card className="flex-1 min-w-80 shadow-none">
									<CardTitle className="border-b-2 p-6">Social</CardTitle>
									<CardContent className="space-y-2 my-4">
										<Card>
											<CardContent className="pt-2">
												<CardTitle className="flex justify-between items-center">
													<span>Add website</span>
													<Button variant="link">Add</Button>
												</CardTitle>
												<CardDescription>Add a website so buyers can learn more about your business</CardDescription>
											</CardContent>
										</Card>
										<Card>
											<CardContent className="pt-2">
												<CardTitle className="flex justify-between items-center">
													<span>Add social media</span>
													<Button variant="link">Add</Button>
												</CardTitle>
												<CardDescription>Add links to your social accounts</CardDescription>
											</CardContent>
										</Card>
									</CardContent>
								</Card>

								{
									user?.userType === "VENDOR"
									&& (
										<Card className="flex-1 min-w-80 shadow-none">
											<CardTitle className="border-b-2 p-6">
												Business Details
											</CardTitle>
											<CardContent className="space-y-8 my-4">
												<div className="space-y-8">
													<p className="flex flex-wrap gap-2">
														<span>Business Name:</span>
														<span>{userProfile?.businessName}</span>
													</p>
													<p className="flex flex-wrap gap-2">
														<span>Email address:</span>
														<span>{user?.email}</span>
													</p>
													<p className="flex flex-wrap gap-2">
														<span>Phone number:</span>
														<span>{userProfile?.phoneNumber}</span>
													</p>
												</div>
											</CardContent>
										</Card>
									)
								}
							</div>
						</section>

					</TabsContent>
					<TabsContent value="kyc">
						<section className="mt-8 space-y-2">
							<div className="flex gap-2 flex-wrap">

								<Card className="flex-1 min-w-80 shadow-none max-w-xl">
									<CardTitle className="border-b-2 p-6">KYC</CardTitle>
									<CardContent className="space-y-4 my-4">
										<Card>
											<CardContent className="pt-2">
												<CardTitle className="flex justify-between items-center">
													<span>Address</span>
													<AddressConfirmation />

												</CardTitle>
												<CardDescription>Enter your residential address</CardDescription>
											</CardContent>
										</Card>
										<Card>
											<CardContent className="pt-2">
												<CardTitle className="flex justify-between items-center">
													<span>Bank Verification Number</span>
													<AddBvnDialog />
												</CardTitle>
												<CardDescription>Verify your BVN</CardDescription>
											</CardContent>
										</Card>
										<Card>
											<CardContent className="pt-2">
												<CardTitle className="flex justify-between items-center">
													<span>National Identification Number</span>
													<Button variant="link">Add</Button>
												</CardTitle>
												<CardDescription>Verify your NIN</CardDescription>
											</CardContent>
										</Card>
									</CardContent>
								</Card>

							</div>
						</section>

					</TabsContent>
					<TabsContent value="security">
						<section className="mt-8 space-y-2">

							<div className="flex gap-2 flex-wrap">

								<Card className="flex-1 min-w-80 shadow-none max-w-xl">
									<CardTitle className="border-b-2 p-6">Security</CardTitle>
									<CardContent className="space-y-4 my-4">
										<Card>
											<CardContent className="pt-2">

												<CardTitle className="flex justify-between items-center">
													<span>Transaction Pin</span>
													<Button variant="link">Reset</Button>
												</CardTitle>
												<CardDescription>Use transaction pin to authenticate sensitive activities in your account</CardDescription>
											</CardContent>
										</Card>
										<Card>
											<CardContent className="pt-2">
												<CardTitle className="flex justify-between items-center">
													<span>Two Factor Authentication</span>
													<Button variant="link">Reset</Button>
												</CardTitle>
												<CardDescription>Use authentication app to protect your account.</CardDescription>
											</CardContent>
										</Card>
										<Card>
											<CardContent className="pt-2">
												<CardTitle className="flex justify-between items-center">
													<span>Change Password</span>
													<Button variant="link">Reset</Button>
												</CardTitle>
												<CardDescription>Choose a strong  unique password that only you can know.</CardDescription>
											</CardContent>
										</Card>
									</CardContent>
								</Card>

							</div>
						</section>
					</TabsContent>
				</Tabs>

			</header>
		</div>
	);
}

const bvnForm = z.object({
	bvn: z.coerce.string().min(10, "BVN must be 10 digits").max(11, "BVN must be 11 digits")
});

function AddBvnDialog() {
	const form = useForm({
		resolver: zodResolver(bvnForm),
		defaultValues: {
			bvn: ""
		}
	});

	const handleSubmit = (_data: z.infer<typeof bvnForm>) => {
		console.log(_data);
	};
	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button variant="link">Add</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle className="text-center">Confirm BVN</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form className="py-4">
						<FormField
							control={form.control}
							name="bvn"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Bank verification number *</FormLabel>
									<FormControl>
										<Input {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</form>

					<DialogFooter>
						<div className="flex flex-wrap w-full *:max-md:w-full gap-2 justify-between">
							<DialogClose asChild>
								<Button
									variant="secondary"
									type="submit"
									onClick={() => form.reset()}
								>
									Discard
								</Button>
							</DialogClose>
							<Button type="submit" onClick={form.handleSubmit(handleSubmit)}>Validate</Button>
						</div>
					</DialogFooter>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
